import React, { useEffect, useRef, useState } from 'react';

import { TextField } from '@mui/material';

import Styles from '../lib/Styles';
import classes from './SelectBoxedField.module.scss';

export default function SelectLongitude(props: any) {
  const { selectedLongitude, setSelectedLongitude, onChangeLongitude, readOnly = false, setError, required = false } = props;
  let initialValue = useRef(props.initialValue);
  const cx = new Styles(classes).cx;
  
  const [showHelper, setShowHelper] = useState<boolean>(false);
  const [entryExceedsMaxLength, setEntryExceedsMaxLength] = useState<boolean>(false);
  const [entryExceedsNumericBounds, setEntryExceedsNumericBounds] = useState<boolean>(false);
  const [entryIsInvalid, setEntryIsInvalid] = useState<boolean>(false);
  const maxNumberOfCharacters: number = 20;

  useEffect(() => {
    if (initialValue.current) {
      setSelectedLongitude(initialValue.current);
      initialValue.current = undefined;
    }
  }, [setSelectedLongitude])

  useEffect(() => {
    setError(entryExceedsMaxLength || entryExceedsNumericBounds || entryIsInvalid)
  }, [entryExceedsMaxLength, entryExceedsNumericBounds, entryIsInvalid, setError])

  // Return true if the passed in value represents a numeric value.
  const isNumeric = (n: any) => {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  // Return true if the string is a + or minus sign.
  const onlySignEntered = (n: string) => {
    return n === '+' || n === '-';
  }

  // Only numeric key input will trigger this handleChange method.
  // Checks for the following:
  //  Entry is not numeric
  //  Entry exceeds the allowed range
  //  Entry exceeds the allowed number of characters
  const handleLongitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value: string = event.target.value;

    if (value.length === 0) {
      setEntryIsInvalid(false);
      setEntryExceedsMaxLength(false);
      setEntryExceedsNumericBounds(false);
    } else {
      // Only allow text with numbers and spaces to be pasted.  The handleKeyPress() will only allow numbers
      // and spaces to be entered.  But if the user Pastes content - we then catch that here, to allow only
      // numbers or spaces.  No error is displayed.
      if (!(/^(\d|\.|\+|-)+$/.test(value)) || (!onlySignEntered(value) && !isNumeric(value))) {
        setEntryIsInvalid(true);
        setEntryExceedsMaxLength(false);
        setEntryExceedsNumericBounds(false);
      } else if (value.length > maxNumberOfCharacters) {
        setEntryExceedsMaxLength(true);
        setEntryIsInvalid(false);
        setEntryExceedsNumericBounds(false);
      } else if (parseFloat(value) > 180.0 || parseFloat(value) < -180) {
        setEntryExceedsNumericBounds(true);
        setEntryIsInvalid(false);
        setEntryExceedsMaxLength(false);
      } else {
        setEntryExceedsMaxLength(false);
        setEntryExceedsNumericBounds(false);
        setEntryIsInvalid(false);
      }
    }

    setSelectedLongitude(value.length === 0 ? undefined : value);
    onChangeLongitude();
  };

  // Only allow plus, minus, decimal, and numeric keys to be processed by onChange handler.
  // PASTE can still add in more digits, but seems a bit overkill to add in guard code for that right now.
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ("+-.0123456789".indexOf(event.key) < 0) {
      event.preventDefault();
    }
  };

  const getHelperText = () => {
    if (entryExceedsMaxLength) {
      return `Only ${maxNumberOfCharacters} digits allowed.`;
    } else if (entryIsInvalid) {
      return 'Invalid longitude'
    } else if (entryExceedsNumericBounds) {
      return 'Longitude must be between +/- 180 degrees.'
    } else {
      return '';
    }
  }

  return (
    <form className={cx('root')} noValidate autoComplete="off">
      <div>
        <TextField
          disabled={readOnly}
          error={entryIsInvalid || entryExceedsMaxLength || entryExceedsNumericBounds}
          helperText={getHelperText()}
          id="longitude"
          label="Longitude"
          variant="outlined"
          value={selectedLongitude || ''}
          onInput={handleLongitudeChange}
          onFocus={() => setShowHelper(true)}
          onKeyPress={handleKeyPress}
          onBlur={() => setShowHelper(false)}
          placeholder={(showHelper && !selectedLongitude) ? "Enter longitude between +180 and -180 degrees." : ''}
          required={required}
          type='text'
        />
      </div>
    </form>
  );
}